/**
 * Route guards.
 *
 * <RequireAuth>      — bounces to /workbench (login screen) when not signed in.
 * <RequireRole role> — 403 placeholder when the current user lacks the role.
 *
 * Both are pure wrappers; they read auth state from useAuth() and
 * never mutate it.
 */
import type { ReactNode } from 'react';

import { useAuth } from './useAuth';
import { useLang } from '@/i18n/useLang';
import { LoginPage } from '@/pages/LoginPage';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const { lang } = useLang();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-stone-400">
        {lang === 'hi' ? 'लोड हो रहा है…' : 'Loading…'}
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <>{children}</>;
}

export function RequireRole({
  role,
  children,
}: {
  role: 'professor' | 'student';
  children: ReactNode;
}) {
  const { user } = useAuth();
  const { lang } = useLang();

  if (!user) return null; // RequireAuth handles the login bounce
  if (user.role !== role) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-md rounded-2xl bg-stone-900/80 p-6 text-center ring-1 ring-stone-800">
          <h2 className="text-lg font-semibold text-saffron-200">
            {lang === 'hi' ? 'अनुमति नहीं' : 'Not allowed'}
          </h2>
          <p className="mt-2 text-sm text-stone-400">
            {lang === 'hi'
              ? 'इस पृष्ठ के लिए आपकी भूमिका अधिकृत नहीं है।'
              : `This page requires the '${role}' role.`}
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
